/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2021, 2025, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import * as _ from 'underscore';
import { Logger, ModalService } from '@core/lib/services';
import { Injectable } from '@angular/core';
import { NPRAuthentication } from '@app/di/security/npr-authentication.service';
import { DiConfigService } from '@app/di/di-config.service';
import { DiService } from '@app/di/di.service';
import { EventMessage, Events, EventService } from '@app/common/event';
import { MessageService } from '@core/lib/services/message.service';
import { IntegrationStatusService } from '@app/di/integration-status.service';
import { AppConfigService } from '@app/common/app-config.service';
import { NotifyService } from '@app/common/notify.service';
import { TranslateService } from '@ngx-translate/core';
import { UtilService } from '@app/util/util.service';
import { appConstants } from '@app/common/app-constants';
import { ReportContextService } from '@app/di/integrations/reporting/report-context.service';
import { ReportSupportService } from '@app/di/integrations/reporting/report-support.service';
import { UserPrivilegesService } from '@app/common/user-privileges.service';
import { EventWorkItem } from '@app/data-model/event-work-item';
import { SearchFilterCriteriaService } from '@app/ui/search/search-filter-criteria.service';
import { UserPreferenceService } from '@app/common/user-preference.service';
import { NPRDataService } from '@app/di/integrations/utils/npr-bridge.service';
import {
  SRClientError,
  SRClientReportOperations,
  SRClientService
} from '@app/di/integrations/reporting/smartreports/sr-client.service';
import { SREditorService } from '@app/di/integrations/reporting/smartreports/sr-editor.service';
import { SRSettingsService } from '@app/di/integrations/reporting/smartreports/sr-settings.service';
import { SRAdminSettingsService } from '@app/di/integrations/reporting/smartreports/sr-admin-settings.service';
import { ModalRef } from '@core/lib/components/modal/modal-ref';
import { Subscription } from 'rxjs';
import { REPORT_STATUS, REPORTING_EVENT } from '@app/di/integrations/reporting/reporting-contants';
import { NPRAuditService } from '@app/di/integrations/utils/npr-audit.service';
import { AuditService } from '@app/common/audit.service';
import { TabContextService } from '@app/ui/client-cache/tab-context.service';
import { PostMessengerService } from '@app/di/channel/window/post-messenger.service';
import { SignOutService } from '@app/common/sign-out.service';

const SERVICE_STATUS = appConstants.dictationIntegrationStatus;

/* eslint-disable */
const enum WORKING_STATUS {
  OK = 0,
  NO_PERMISSION = 1,
  NO_ACCESS_TOKEN = 2,
  NO_NPR_SERVICE = 4,
  NO_SR_SERVICE = 8
}
/* eslint-enable */

const SR_PERMISSIONS_MAP = {
  AD_NPR_Install: 'technicalAdminAccess', // - system configurations "openAdminPanel"
  AD_NPR_Management: ['clinicalAdminAccess', 'systemTechnicalAdmin'], // - clinical/department configurations "openAdminPanel"
  NPR_createReport: 'createReport',
  NPR_openReport: 'openReport',
  NPR_openReportReadOnly: 'openReportReadOnly', // - permission to use SR in a read-only mode
  NPR_savePrelim: 'prelimReport',
  NPR_publishPrelim: 'distributeReport',
  NPR_signReport: 'signReport',
  NPR_addendReport: 'addendReport',
  NPR_VR: 'dictateReport', // - permission to use voice recognition when editing a report
  NPR_printReport: 'printReport',
  NPR_deleteReport: 'deleteReport',
  ID_takeControl: 'takeControl'
} as const;

const enum NPR_PRIVILEGES {
  USE_IN_WFM = 'ID_NPR',
  CREATE = 'NPR_createReport',
  OPEN = 'NPR_openReport',
  SAVE_PRELIM = 'NPR_savePrelim',
  PUBLISH_PRELIM = 'NPR_publishPrelim',
  SIGN = 'NPR_signReport',
  ADDEND = 'NPR_addendReport'
}

const enum REPORT_BUTTON_LABELS {
  report = 'report.lbl.report',
  createReport = 'report.lbl.createReport',
  editReport = 'report.lbl.editReport',
  addendReport = 'report.lbl.addendReport',
  editAddendReport = 'report.lbl.editAddendReport'
}

let INFO_MISMATCH_WARNINGS = [];

const enum INFO_MISMATCH {
  EXAM_ACCESSION = 1,
  PATIENT_MRN = 2,
  ALL = 3
}

const radiologist = {
  id: undefined,
  userName: '',
  domain: '',
  privileges: undefined,
  isAllowedTo: function (...perms: string[]) {
    for (let i = 0; i < perms.length; i++)
      if (this.privileges.includes(perms[i])) {
        return true;
      }
    return false;
  }
};

const reportPermisionRules = {
  U: {
    // Unknown
    isAllowed: function () {
      return radiologist.isAllowedTo(NPR_PRIVILEGES.CREATE);
    },
    label: [REPORT_BUTTON_LABELS.createReport]
  },
  P: {
    // Pending
    isAllowed: function (isOwner) {
      return isOwner && radiologist.isAllowedTo(NPR_PRIVILEGES.CREATE);
    },
    label: [REPORT_BUTTON_LABELS.editReport]
  },
  D: {
    // Draft
    isAllowed: function (isOwner) {
      return isOwner || radiologist.isAllowedTo(NPR_PRIVILEGES.OPEN);
    },
    label: [REPORT_BUTTON_LABELS.editReport, REPORT_BUTTON_LABELS.editReport]
  },
  T: {
    // Prelim
    isAllowed: function (isOwner) {
      return (
        (isOwner || radiologist.isAllowedTo(NPR_PRIVILEGES.OPEN)) &&
        radiologist.isAllowedTo(NPR_PRIVILEGES.SIGN, NPR_PRIVILEGES.PUBLISH_PRELIM, NPR_PRIVILEGES.SAVE_PRELIM)
      );
    },
    label: [REPORT_BUTTON_LABELS.editReport, REPORT_BUTTON_LABELS.editReport]
  },
  TD: {
    // Prelim Distributed
    isAllowed: function (isOwner) {
      return (
        (isOwner || radiologist.isAllowedTo(NPR_PRIVILEGES.OPEN)) &&
        radiologist.isAllowedTo(NPR_PRIVILEGES.SIGN, NPR_PRIVILEGES.PUBLISH_PRELIM)
      );
    },
    label: [REPORT_BUTTON_LABELS.editReport, REPORT_BUTTON_LABELS.editReport]
  },
  F: {
    // Final
    isAllowed: function () {
      return radiologist.isAllowedTo(NPR_PRIVILEGES.ADDEND);
    },
    label: [REPORT_BUTTON_LABELS.addendReport, REPORT_BUTTON_LABELS.addendReport]
  },
  PA: {
    // Addendum Pending
    isAllowed: function (isOwner) {
      return isOwner && radiologist.isAllowedTo(NPR_PRIVILEGES.ADDEND);
    },
    label: [REPORT_BUTTON_LABELS.editAddendReport, REPORT_BUTTON_LABELS.addendReport]
  },
  DA: {
    // Addendum Draft
    isAllowed: function (isOwner) {
      return isOwner || radiologist.isAllowedTo(NPR_PRIVILEGES.ADDEND);
    },
    label: [REPORT_BUTTON_LABELS.editAddendReport, REPORT_BUTTON_LABELS.editAddendReport]
  },
  TA: {
    // Addendum Prelim
    isAllowed: function () {
      return (
        radiologist.isAllowedTo(NPR_PRIVILEGES.ADDEND) &&
        radiologist.isAllowedTo(NPR_PRIVILEGES.SIGN, NPR_PRIVILEGES.PUBLISH_PRELIM, NPR_PRIVILEGES.SAVE_PRELIM)
      );
    },
    label: [REPORT_BUTTON_LABELS.editAddendReport, REPORT_BUTTON_LABELS.editAddendReport]
  },
  S: {
    // Signed status
    isAllowed: function () {
      return radiologist.isAllowedTo(NPR_PRIVILEGES.ADDEND);
    },
    label: [REPORT_BUTTON_LABELS.addendReport, REPORT_BUTTON_LABELS.addendReport]
  }
};

const reportStatusIdMap = {
  0: 'U', // Unknown
  1: 'U', // Associating
  7000: 'T', // Prelim
  7005: 'T', // Prelim Temporary
  7001: 'TD', // Prelim Distributed
  7100: 'P', // Pending
  7105: 'P', // Pending Temporary
  7500: 'D', // Draft
  7505: 'D', // Draft Temporary
  9000: 'F', // Final
  9001: 'PA', // Addendum Pending
  9500: 'DA', // Addendum Draft
  9900: 'TA', // Addendum Prelim
  9901: 'TA', // Addendum Prelim Distributed,
  10000: 'S' // Signed
};

interface IReportButtonState {
  isEnabled: boolean;
  label: string;
}

const log = new Logger('SRService');

@Injectable({
  providedIn: 'root'
})
export class SRService {
  private _closeViewerCallback: any;
  private _closeISiteViewer: any;
  private _checkViewerContextCallback: any;
  private callBackOnViewerClose: any;

  private serviceStatus = SERVICE_STATUS.None;
  private currentStatus = WORKING_STATUS.OK;

  private editor: SREditorService;
  private settings: SRSettingsService;
  private adminSettings: SRAdminSettingsService;
  private confirmation: ModalRef;

  private defaultLocaleSupported = true;
  private workingLocale = 'en-US';
  private srConfig = {} as any;

  private afterSyncReport = (reportId, examId, status) => {
    const report = this.workingItems.get('' + reportId);
    if (report) {
      report.status = status;
    }
  };

  private siteInfo = {
    name: 'GEHC',
    department: 'radiology',
    allowExceptionReport: true
  };

  constructor(
    private authenticator: NPRAuthentication,
    private dataService: NPRDataService,
    private dataLookup: SearchFilterCriteriaService,
    private diConfigService: DiConfigService,
    private diService: DiService,
    private reportSupportService: ReportSupportService,
    private userPrivilegesService: UserPrivilegesService,
    private eventService: EventService,
    private uiEventService: MessageService,
    private statusService: IntegrationStatusService,
    private appConfigService: AppConfigService,
    private notify: NotifyService,
    private translate: TranslateService,
    private reportContextService: ReportContextService,
    private utilService: UtilService,
    private preferences: UserPreferenceService,
    private modalService: ModalService,
    private nprAuditService: NPRAuditService,
    private signOutService: SignOutService,
    private auditService: AuditService,
    private tabContextService: TabContextService,
    postMessengerService: PostMessengerService
  ) {
    this.editor = new SREditorService(this.authenticator, postMessengerService);
    this.settings = new SRSettingsService(this.authenticator, preferences, notify, translate, postMessengerService);
    this.adminSettings = new SRAdminSettingsService(
      this.authenticator,
      preferences,
      notify,
      translate,
      postMessengerService
    );
  }

  initialize() {
    log.info('initialize NPR service');

    this.authenticator.onsync = () => this.tokenSync();
    this.authenticator.onready = () => {
      this.clearCurrentStatus(WORKING_STATUS.NO_ACCESS_TOKEN);
      this.healthReporter.showReady();
    };
    this.authenticator.onfailure = () => {
      this.setCurrentStatus(WORKING_STATUS.NO_ACCESS_TOKEN);
      this.healthReporter.showFailed();
    };

    this.settings.onerror = (response: any) => this.clientErrorHandler(response);
    this.adminSettings.onerror = (response: any) => this.clientErrorHandler(response);
    this.editor.onerror = (response: any) => this.clientErrorHandler(response);
    this.editor.onevent = (response: any) => this.editorEventHandler(response);
    this.editor.onclose = () => this.editorCloseHandler();
    this.signOutService.registerCallback(this.cleanup);
    radiologist.userName = this.appConfigService.getUserId();
    radiologist.domain = this.appConfigService.getUserDefaultDomain();
    radiologist.privileges = this.userPrivilegesService.getUserPrivileges();

    INFO_MISMATCH_WARNINGS = [
      '',
      this.translate.instant('di.desktopIntegration.npr.warning.exam.mismatch'),
      this.translate.instant('di.desktopIntegration.npr.warning.patient.mismatch'),
      this.translate.instant('di.desktopIntegration.npr.warning.info.mismatch')
    ];

    const preloadListener = this.uiEventService.on('preloadSearchCriteria.done').subscribe(() => {
      preloadListener.unsubscribe();

      this.dataService.init();
      this.dataService.afterSyncReport = this.afterSyncReport;

      const rad = this.appConfigService.getUserDto();
      radiologist.id = rad.userId;
      const permissions = radiologist.privileges
        .filter((p: string) => SR_PERMISSIONS_MAP[p] !== undefined)
        .flatMap((p: string) => SR_PERMISSIONS_MAP[p]);
      if (radiologist.isAllowedTo(NPR_PRIVILEGES.USE_IN_WFM)) {
        this.authenticator.createToken(
          {
            id: radiologist.userName,
            firstname: rad.firstName,
            lastname: rad.lastName
          },
          permissions
        );
      } else {
        this.setCurrentStatus(WORKING_STATUS.NO_PERMISSION);
        this.healthReporter.showFailed();
        this.setSystemError('di.desktopIntegration.error.no.reporting.privilege', '', '', true);
      }
    });

    const serviceChecker = () =>
      this.dataService.getConfiguration().then(
        config => {
          log.debug('Configuration', config);
          this.clearCurrentStatus(WORKING_STATUS.NO_NPR_SERVICE);
          const url = config.serviceUrl.match(/(https?:\/\/[^\/]+)/i)[1].toLowerCase();
          const lightsMode = !!this.preferences.getUserLevelPreferenceFromCache('LightsMode');
          const requestTimeout = this.diConfigService.REPORTING_CONFIG.SETCONTEXT_TIMEOUT;
          this.siteInfo.name = config.site;
          this.siteInfo.department = config.department;
          this.srConfig.apiVersion = config.apiVersion;
          this.srConfig.issueridRequired =
            config.issueridRequired === false || config.issueridRequired === 'false' ? false : true;
          this.srConfig.mappedLanguages = this.setMappedLanguages(config.mappedLanguages);
          this.srConfig.supportedLanguages = this.setSupportedLanguages(config.supportedLanguages);
          this.setWorkingLocale();
          this.checkIfDefaultDeparmentExist();
          SRClientService.register(url, this.workingLocale, lightsMode ? 'light' : 'dark', requestTimeout);
          const srServiceChecker = () =>
            this.dataService
              .checkClientService(url)
              .then(_ready => {
                this.clearCurrentStatus(WORKING_STATUS.NO_SR_SERVICE);
                this.clearSystemError('sr.service');
                this.healthReporter.showReady();
              })
              .catch(error => {
                log.error('Reporting system is not responding. Please check if the access is blocked. Error:', error);
                this.setCurrentStatus(WORKING_STATUS.NO_SR_SERVICE);
                this.healthReporter.showFailed();
                this.setSystemError('di.desktopIntegration.error.reporting.servererror', '', '', true, 'sr.service');
                log.error('Retry checking in 30 seconds');
                const checkerTimeout = setTimeout(() => {
                  srServiceChecker();
                  clearTimeout(checkerTimeout);
                }, 30_000);
              });
          srServiceChecker();
        },
        error => {
          log.error('Failed to load configuration from Native PACS Reporting service:', error);
          this.setCurrentStatus(WORKING_STATUS.NO_NPR_SERVICE);
          this.healthReporter.showFailed();
          log.error('Retry loading configuration in 30 seconds');
          const timeout = setTimeout(() => {
            serviceChecker();
            clearTimeout(timeout);
          }, 30_000);
        }
      );
    serviceChecker();
  }

  private setMappedLanguages(mappedLanguages: string) {
    let mapLanguage;
    try {
      mapLanguage = JSON.parse(mappedLanguages.replace(/'/gi, '"'));
    } catch (e) {
      log.error('Error while parsing mapped languages ', e);
      mapLanguage = {};
    }
    log.debug('Mapped language set as ', mapLanguage);
    return mapLanguage;
  }

  private setSupportedLanguages(supportedLanguages: string) {
    let supLanguage = [];
    try {
      supLanguage = JSON.parse(supportedLanguages.replace(/'/gi, '"'));
    } catch (e) {
      log.error('Error while parsing support languages ', e);
      supLanguage.push(appConstants.DEFAULT_LANGUAGE);
    }
    log.debug('Supported language set as ', supLanguage);
    return supLanguage;
  }

  private setWorkingLocale() {
    const language = this.srConfig.mappedLanguages[this.translate.currentLang] || this.translate.currentLang;
    log.debug('Supported language are: ', this.srConfig.supportedLanguages);
    if (!this.srConfig.supportedLanguages.includes(language)) {
      this.defaultLocaleSupported = false;
      const langTimeout = setTimeout(() => {
        this.setSystemError('di.desktopIntegration.npr.warning.unsupported.language', '[locale_id]', language);
        clearTimeout(langTimeout);
      }, 5_000);
      this.healthReporter.showUnsupportedLanguage();
      log.debug('Unsupported language ', language);
    } else {
      this.workingLocale = language;
      log.debug('Working language set as ', this.workingLocale);
    }
  }

  private setSystemError(id: string, placeholder: string, details: string, showNotification?: boolean, type?: string) {
    if (!type) {
      type = 'reporting.configuration';
    }
    if (id === '') {
      this.statusService.addSystemError(type, '');
      return;
    }
    let message = this.translate.instant(id);
    if (placeholder !== '') {
      message = message.replace(placeholder, details);
    }
    this.statusService.addSystemError(type, message);
    if (showNotification === true) {
      this.notify.error(message, this.translate.instant('di.desktopIntegration.title'));
    }
  }

  private clearSystemError(type?: string) {
    this.statusService.addSystemError(type ? type : 'reporting.configuration', '');
  }

  private setCurrentStatus(status: number) {
    this.currentStatus |= status;
  }

  isServiceDown() {
    if (this.currentStatus === WORKING_STATUS.NO_NPR_SERVICE || this.currentStatus === WORKING_STATUS.NO_ACCESS_TOKEN) {
      return true;
    }
    return false;
  }

  private clearCurrentStatus(status: number) {
    if (this.currentStatus & status) {
      this.currentStatus ^= status;
    }
  }

  setTheme(theme: string) {
    this.editor.setTheme(theme);
    this.settings.setTheme(theme);
    this.adminSettings.setTheme(theme);
  }

  public showSettings() {
    this.settings.show();
  }

  public showAdminSettings() {
    this.adminSettings.show();
  }

  private getPatientInfo(workItem: any) {
    const patientNames = workItem.keys.patientName.split(/,\s/);
    return {
      id: workItem.keys.mrn[0].value,
      firstName: patientNames[1],
      lastName: patientNames[0],
      birthDate: workItem.keys.patientDoB,
      gender: workItem.keys.patientGender
    };
  }

  private clientErrorHandler(response: any) {
    log.debug('clientErrorHandler - got error', response);
    if (response.action === 'deleteReport') {
      const report = this.workingItems.get(response.reportId);
      this.nprAuditService.logDeleteReportFailureEvent(
        report,
        appConstants.applicationServerParticipant.ReportingSystem
      );
    }

    switch (response.errorCode) {
      case SRClientError.SR_SERVICE_TIMEOUT:
        this.healthReporter.showLastFailed();
        this.serviceStatus = SERVICE_STATUS.TimedOut;
        // eslint-disable-next-line no-case-declarations
        const action = this.translate.instant(
          'di.desktopIntegration.npr.request.timeout.action.' + response.message.type
        );
        // eslint-disable-next-line no-case-declarations
        let message = this.translate.instant(
          SRClientReportOperations.includes(response.message.type)
            ? 'di.desktopIntegration.npr.request.timeout.report.error.message'
            : 'di.desktopIntegration.npr.request.timeout.control.error.message'
        );
        message = message.replace('[action]', action);
        // eslint-disable-next-line no-case-declarations
        const tab = this.utilService.getActiveTab();
        if (tab && tab.workItem && tab.workItem.keys) {
          message = message.replace('[accession#]', tab.workItem.keys.procedureAccessionNo);
        } else {
          message = message.replace('[accession#] ', '');
        }
        this.notify.error(message, this.translate.instant('di.desktopIntegration.title'));
        break;
      case SRClientError.SR_RESPONSE_UNPROCESSABLE:
        this.healthReporter.showLastFailed();
        log.debug('Received response from a timedout request:', response.event);
        break;
      case SRClientError.SR_SERVICE_UNREACHABLE:
        log.debug('Reporting service is unavailable:', response.event);
        this.healthReporter.showFailed();
        this.notify.error(
          this.translate.instant('di.desktopIntegration.error.nprServiceError'),
          this.translate.instant('di.desktopIntegration.title')
        );
        break;
      default:
        log.debug('Unhandled error code:', response.errorCode);
        this.healthReporter.showLastFailed();
        break;
    }
    // If the tab is already closed remove the report from working items
    const reportId = response.reportId ? response.reportId : this.editor.getReportId();
    if (reportId) {
      const report = this.workingItems.get(reportId);
      if (report) {
        const tab = this.utilService.getTab(report.examId);
        if (!tab) {
          this.workingItems.remove(reportId);
        }
      }
    }
  }

  private createUpdatedWorkItem(response: any, status?: number) {
    const report = this.workingItems.get(response.reportId);
    const isReportFinal = response.reportStatus === REPORT_STATUS.FINAL;
    log.info(response.action, ':', report);
    if (report) {
      const workItem = new EventWorkItem(report.examId, {
        source: 'desktop-integration'
      });
      if (response.action === 'distributeReport' && !isReportFinal) {
        response.reportStatus = response.isAddended
          ? REPORT_STATUS.ADDENDUM_PRELIM_PUBLISHED
          : REPORT_STATUS.PRELIM_PUBLISHED;
      } else if (response.action === 'saveReport') {
        // This is workaround solution due to inconsistency of SR editor.
        // The editor sends 2 events [saveReport] and [distributeReport] in sequence when clicking on [Publish] button.
        // The first event action [saveReport] indicates that the report is saved to Prelim while the report is already
        // in Published Prelim status, this should never happen. This block of code try to detect this case and return
        // an undefined workItem so that subsequence operations can ignore this action and focus on the following one
        // [distributeReport]. This also avoid duplicated entries in report lifecycle in NPR database.
        // This block should be removed in future when SR fix this issue.
        const newStatus = response.reportStatus;
        if (
          (report.status === REPORT_STATUS.PRELIM_PUBLISHED && newStatus === REPORT_STATUS.PRELIM) ||
          (report.status === REPORT_STATUS.ADDENDUM_PRELIM_PUBLISHED && newStatus === REPORT_STATUS.ADDENDUM_PRELIM)
        ) {
          return undefined;
        }
      }

      if (report.isTemporary) {
        response.reportStatus += REPORT_STATUS.TEMPORARY_SIGN;
      }
      // SR editor send 2 events [saveReport] and [distributeReport] when clicking on 'Finalize' button.
      // This block is written to ignore second event [distributeReport] for final report.
      if (response.action === 'distributeReport' && isReportFinal) {
        return undefined;
      }
      workItem.reportStatus = status !== undefined ? status : response.reportStatus;
      const tab = this.utilService.getTab(report.examId);
      if (tab && tab.workItem && tab.workItem.keys) {
        workItem.keys.timeOfExam = tab.workItem.keys.timeOfExam;
        workItem.keys.procedureStatusId = tab.workItem.keys.procedureStatusId;
      }
      workItem.associationList = this.tabContextService.getAssociatedIds(report.examId);
      workItem.reportId = response.reportId;
      return workItem;
    }
  }

  private getReportExamIds(report) {
    if (report.associatedExamList && report.associatedExamList.length > 0) {
      return report.associatedExamList.map(exam => exam.examId);
    } else {
      return [report.examId];
    }
  }

  private editorEventHandler(response: any) {
    if (this.serviceStatus === SERVICE_STATUS.LaunchApplicationInitiated) {
      this.healthReporter.showReady();
    }
    const report = this.workingItems.get(response.reportId);
    if (response.code === 201) {
      if (report) {
        log.debug('Report for', report.accessionNumber, 'is created');
        report.status = report.isTemporary ? REPORT_STATUS.PENDING_TEMPORARY : REPORT_STATUS.PENDING;
      }
    } else if (response.action === 'saveReport' || response.action === 'distributeReport') {
      const workItem = this.createUpdatedWorkItem(response);
      if (workItem) {
        this.eventService.sendEvent(new EventMessage(Events.savereport, workItem));
        this.reportSupportService.syncSavedExamsReportStatus(workItem);
        this.nprAuditService.logSaveReportSuccessEvent(
          report,
          appConstants.applicationServerParticipant.ReportingSystem
        );
      }
    } else if (response.action === 'deleteReport') {
      const status = report.status >= REPORT_STATUS.FINAL ? REPORT_STATUS.DISCARD_ADDENDUM : REPORT_STATUS.DISCARD;
      const workItem = this.createUpdatedWorkItem(response, status);
      report.status = REPORT_STATUS.UNKNOWN;
      // Publish report close success event
      this.publishReportingEvent(REPORTING_EVENT.CLOSE, report, false, appConstants.SUCCESS);
      this.workingItems.remove(report.reportId);
      this.editor.clear(report.reportId);
      const tab = this.utilService.getTab(report.examId);
      if (workItem && tab) {
        this.eventService.sendEvent(new EventMessage(Events.closediscard, workItem));
      }
      const examIds = this.getReportExamIds(report);
      this.dataService.syncReportStatus(undefined, examIds, 0);
      this.nprAuditService.logDeleteReportSuccessEvent(
        report,
        appConstants.applicationServerParticipant.ReportingSystem
      );
    } else if (response.action === 'deleteAddendum') {
      this.postDeleteHandler(response, report, true);
    } else if (response.action === 'deleteRevision') {
      this.postDeleteHandler(response, report, false);
    } else if (response.action === 'closeReport') {
      const workItem = this.createUpdatedWorkItem(response, report.status);
      if (workItem) {
        this.eventService.sendEvent(new EventMessage(Events.close, workItem));
        // Publish report close success event
        this.publishReportingEvent(REPORTING_EVENT.CLOSE, report, false, appConstants.SUCCESS);
        this.workingItems.remove(response.reportId);
        this.editor.clear(response.reportId);
      }
    } else {
      if (response.description === 'The report was closed') {
        // Publish report close success event
        this.publishReportingEvent(REPORTING_EVENT.CLOSE, report, false, appConstants.SUCCESS);
        this.workingItems.remove(response.reportId);
        this.editor.clear(response.reportId);
      } else if (response.description === 'The addendum was deleted') {
        this.dataService.saveReport(report.reportId, report.accessionNumber, report.examId, REPORT_STATUS.FINAL);
        const examIds = this.getReportExamIds(report);
        this.dataService.syncReportStatus(report.reportId, examIds, REPORT_STATUS.FINAL);
        report.status = REPORT_STATUS.FINAL;
        // Publish report close success event
        this.publishReportingEvent(REPORTING_EVENT.CLOSE, report, false, appConstants.SUCCESS);
        this.workingItems.remove(response.reportId);
      }
    }
  }

  private postDeleteHandler(response: any, report: any, isAddendum: boolean) {
    const workItem = this.createUpdatedWorkItem(response, REPORT_STATUS.FINAL);
    if (workItem) {
      if (isAddendum) {
        this.nprAuditService.logReportAddendumDeleteSuccessEvent(
          report,
          appConstants.applicationServerParticipant.ReportingSystem
        );
      }
      report.status = REPORT_STATUS.FINAL;
      // Publish report close success event
      this.publishReportingEvent(REPORTING_EVENT.CLOSE, report, false, appConstants.SUCCESS);
      this.workingItems.remove(response.reportId);
      this.editor.closeReport().catch(error => log.error('close report failed : ', error));
      this.eventService.sendEvent(new EventMessage(Events.close, workItem));
      const examIds = this.getReportExamIds(report);
      this.dataService.syncReportStatus(response.reportId, examIds, REPORT_STATUS.FINAL);
    }
  }

  private editorCloseHandler() {
    const id = this.reportContextService.getCurrentRequestedProcId();

    this.clearContext();

    if (!this.workingItems.isEmpty()) {
      if (id) {
        const report = this.workingItems.find(id);
        if (report) {
          this.closeItem(report, 'sr-editor-close');
          if (report.status === REPORT_STATUS.TEMPLATE_SELECTING) {
            // Publish report open failure event
            this.publishReportingEvent(REPORTING_EVENT.OPEN, report, false, appConstants.FAILURE);
          } else {
            // Publish report close success event
            this.publishReportingEvent(REPORTING_EVENT.CLOSE, report, false, appConstants.SUCCESS);
          }
        }
        this.uiEventService.broadcast('removeTab', id);
      }
    }
  }

  private clearContext() {
    if (this.authenticator.isFailed()) {
      this.healthReporter.showFailed();
    } else {
      this.healthReporter.showReady();
    }
    this.resetIntegration();
  }

  // #region status checking
  private isSaveOnTabClosure() {
    return this.preferences.getUserPreferenceFromCache('save-report-on-tab-closure');
  }

  // #Added for take control
  private isSaveOnReleaseControl() {
    return this.preferences.getUserPreferenceFromCache('save-report-on-release-control') === 'save';
  }

  private isTemporary(status: number): boolean {
    return status % REPORT_STATUS.TEMPORARY_MASK === REPORT_STATUS.TEMPORARY_SIGN;
  }

  private isPending(status: number) {
    return status - (status % REPORT_STATUS.TEMPORARY_MASK) === REPORT_STATUS.PENDING;
  }

  private isPendingOrTemplateSelecting(status: number) {
    return status === REPORT_STATUS.TEMPLATE_SELECTING || this.isAddendumPending(status) || this.isPending(status);
  }

  private isNotPending(status: number) {
    return !this.isPending(status) && !this.isAddendumPending(status);
  }

  private isNotFinal(status: number) {
    return status !== REPORT_STATUS.FINAL;
  }

  private isAddendumPending(status: number) {
    return status === REPORT_STATUS.ADDENDUM_PENDING;
  }
  // #endregion

  // #region Viewer callbacks
  setCloseViewerCallback(callback) {
    this._closeViewerCallback = callback;
  }

  setiSIteViewerCloseCallBack(callback) {
    this._closeISiteViewer = callback;
  }

  /**
   * Method to set check viewer context callback
   */
  setCheckViewerContextCallback(callback) {
    this._checkViewerContextCallback = callback;
  }

  /**
   * Method to execute viewer closed callback function
   */
  invokeViewerClosedCallback() {
    if (this.callBackOnViewerClose) {
      log.info('invokeViewerClosedCallback - this.callBackOnViewerClose executing');
      this.callBackOnViewerClose();
      this.callBackOnViewerClose = undefined;
    }
  }
  //#endregion

  /**
   * Method to return this.dictationStatus
   */
  public getDictationStatus() {
    if (this.serviceStatus !== SERVICE_STATUS.Disabled && this.authenticator.isFailed()) {
      this.healthReporter.showFailed();
    }
    return this.serviceStatus;
  }

  /**
   * Method to check whether integration is loading or not
   */
  public isIntegrationLoading(currentAction) {
    let status = false;
    if (this.serviceStatus === SERVICE_STATUS.LaunchApplicationInitiated) {
      if (currentAction !== 'preview' && currentAction !== 'closediscard') {
        this.notify.warning(
          this.translate.instant('di.desktopIntegration.warning.dictationIsInitializing'),
          this.translate.instant('di.desktopIntegration.title')
        );
      }
      status = true;
    }
    return status;
  }

  public resetIntegration() {
    this.reportSupportService.clearMessageQueue();
    this.reportSupportService.clearMessageWaitingForResponse();
    // Clear the report setcontext timeout
    this.reportSupportService.clearSetContextTimeout();
    this.reportContextService.removeContext(this.reportContextService.getCurrentRequestedProcId());
  }

  /**
   * Method to reset reporting application state after sending clear/close action.
   */
  public resetApplicationState(context) {
    try {
      if (context.id === undefined) {
        log.error('resetApplicationState - workitem id is undefined');
        return;
      }
      this.reportSavedEventHandler(context.id);
    } catch (error) {
      log.error('failed on resetApplicationState with', error);
    }
  }

  private cleanup = () => {
    try {
      log.info('Start NPR integration cleanup');
      this.editor.close();
      this.settings.close();
      this.adminSettings.close();
      if (!this.workingItems.isEmpty()) {
        const requestId = 'cleanup-on-signout';
        if (!this.isSaveOnTabClosure()) {
          this.dataService.discardReports(this.workingItems.values(), requestId);
        } else {
          const saveItems = [];
          this.workingItems.values().forEach(report => {
            if (this.isNotPending(report.status) && this.isNotFinal(report.status)) {
              saveItems.push(report);
            }
          });
          if (saveItems.length > 0) {
            this.dataService.saveReports(saveItems, requestId);
          }
        }
        this.workingItems.clear();
      }
    } catch (error) {
      log.error('error during cleanup:', error);
      return false;
    }
  };

  /**
   * Report saved event handler
   *
   */
  reportSavedEventHandler(contextId) {
    const currentId = this.reportContextService.getCurrentRequestedProcId();
    if (contextId === currentId) {
      this.reportContextService.removeContext(contextId);
      if (!this.diService.isLogoutInitiated()) {
        this.editor
          .closeReport()
          .then(result => {
            if (result !== 'empty') {
              this.workingItems.remove(result.reportId);
            }
          })
          .catch(error => log.error('close report failed : ', error));
      }
    } else {
      log.info(
        'reportSavedEventHandler - currentContext not equal to new context - param contextid = ' +
          contextId +
          ' currentContext = ' +
          currentId
      );
    }
  }

  /**
   * Method to handle launch readonly action.
   */
  public launchReadonly(context) {
    log.info('launchreadonly event handler - Study Id : ' + context.id);
    this.preview(context);
  }

  /**
   * This method is invoked on activating an exam from a worklist.
   * The responsibility of this method is to: Open report based on user preference.
   *
   * target - selected exam's details
   */
  public read(target) {
    try {
      if (this.workingItems.find(target.id)) {
        this.launchDictation(target);
        return;
      }
      const statusId = target.keys && !_.isUndefined(target.keys.reportStatus) ? target.keys.reportStatus : 0;
      const state = this.getReportBtnState({
        reportStatus: statusId,
        reportAuthor: target.keys.authorId,
        reportText: undefined,
        approvalTime: undefined,
        approverId: undefined,
        lastAuthorId: this.reportButtonInfo.tracker.lastAuthorId
          ? this.reportButtonInfo.tracker.lastAuthorId
          : target.keys.authorId
      });

      if (state.isEnabled) {
        this.launchDictation(target);
      } else {
        const status = _.findWhere(this.dataLookup.getReportStatuses(), { id: statusId });
        log.info('User', radiologist.userName, 'has no privileges for the current report status:', status.name);
      }
    } catch (error) {
      log.error(
        'failed on read action handler - Accession : ' + target.keys.procedureAccessionNo + ' with error:',
        error
      );
    }
  }

  /**
   * This method is invoked on preview an exam from a worklist.
   *
   * target - selected exam's details
   */
  public preview(target) {
    try {
      if (target && target.id === this.reportContextService.getCurrentRequestedProcId()) {
        log.info('Skip launchReport');
        if (this.editor.isLoaded()) {
          this.editor.focus();
        }
      } else {
        const report = this.workingItems.find(target.id);
        const accession = target.keys.procedureAccessionNo;
        const associationList = this.getCandidatesForAssociation(target);
        // If report is in TEMPLATE_SELECTING stage then only call createReport for SR otherwise request to open report
        if (report && report.status === REPORT_STATUS.TEMPLATE_SELECTING) {
          this.createReport(accession, associationList, target);
        } else if (!report) {
          this.editor.reset();
        } else {
          this.editor.openReport(report.reportId).catch(error => log.error('open report failed : ', error));
        }

        const id = this.reportContextService.getCurrentRequestedProcId();
        const previousReport = this.workingItems.find(id);
        if (previousReport) {
          // Publish previous report close success event
          this.publishReportingEvent(REPORTING_EVENT.CLOSE, previousReport, true, appConstants.SUCCESS);
        }
        const currentAccession = this.reportContextService.getCurrentAccession();
        if (currentAccession !== '') {
          this.reportContextService.setCurrentContext(target.id, '');
        }
      }
    } catch (error) {
      log.error('failed preview for accession ' + target.keys.procedureAccessionNo + ' with error:', error);
    }
  }

  /**
   * Subscribe the closediscard operation from diagnostic-hub.
   * The responsibility of this method is to: Discard the report
   *
   * target - selected exam's details
   */
  public closeDiscardReport(target) {
    try {
      const isCurrentReport = this.reportContextService.checkReportInContext(target.id);
      const currentExamId = this.reportContextService.getCurrentRequestedProcId();
      // Close request for a hidden tab
      if (isCurrentReport && currentExamId !== target.id) {
        log.info('closeDiscard - close request for a hidden context is received study id:', target.id);
        const report = this.workingItems.find(target.id);
        if (report) {
          log.debug('closeDiscard - report =', report);
          this.closeItem(report, 'close-inactive-tab');
        }
        this.reportContextService.removeContext(target.id);
        return false;
      }

      // Close request for exam not in context
      if (!isCurrentReport) {
        log.info('closeDiscard -', target.id, '- Study not in context');
        if (currentExamId === '') {
          //
        }
        return false;
      }
      const report = this.workingItems.find(target.id);
      if (report) {
        log.info('closeDiscard -', target.id, ', report:', report);
        // Save or Discard report based on user preference
        if (target.keys.source === 'diagnostic-hub-tab') {
          if (report.status === REPORT_STATUS.TEMPLATE_SELECTING) {
            log.info('delete pre-creating report in template selection step');
            this.dataService.deleteReport(report.reportId, report.accessionNumber, report.examId).then(
              _ => {
                this.workingItems.remove(report.reportId);
                this.nprAuditService.logDeleteReportSuccessEvent(report, appConstants.applicationServerParticipant.NPR);
              },
              error => {
                this.nprAuditService.logDeleteReportFailureEvent(report, appConstants.applicationServerParticipant.NPR);
                log.error('Error while deleting report', error);
              }
            );
          } else {
            if (target.actionType == 'externalKeep' || target.actionType == 'defaultKeep') {
              this.saveReport(report);
            } else {
              this.discardReport(report);
            }
          }
        } else {
          // Disacrd report
          this.discardReport(report);
        }
      }
      this.reportContextService.removeContext(target.id);
      return false;
    } catch (error) {
      log.error('Failed on closeDiscard event with error:', error.description);
    }
  }

  private discardReport(report) {
    if (this.isPending(report.status) || this.isAddendumPending(report.status)) {
      this.editor.deleteReport().then(
        () => {
          if (this.isPending(report.status)) {
            const examIds = this.getReportExamIds(report);
            this.dataService.syncReportStatus(undefined, examIds, 0);
          }
        },
        error => {
          this.nprAuditService.logDeleteReportFailureEvent(report, appConstants.applicationServerParticipant.NPR);
          log.debug('delete failed', error);
          // Publish report closed failure event
          this.publishReportingEvent(REPORTING_EVENT.CLOSE, report, false, appConstants.FAILURE);
        }
      );
    } else {
      this.editor
        .discardReport()
        .then(_reponse => {
          this.editor.closeReport().catch(error => {
            log.error('close report failed : ', error);
            // Publish report close failure event
            this.publishReportingEvent(REPORTING_EVENT.CLOSE, report, false, appConstants.FAILURE);
          });
        })
        .catch(error => {
          log.error('discard report failed : ', error);
          // Publish report close failure event
          this.publishReportingEvent(REPORTING_EVENT.CLOSE, report, false, appConstants.FAILURE);
        });
    }
  }

  private saveReport(report) {
    if (this.isPending(report.status) || this.isAddendumPending(report.status)) {
      this.editor.closeReport().catch(error => {
        log.error('close report failed : ', error);
        // Publish report close failure event
        this.publishReportingEvent(REPORTING_EVENT.CLOSE, report, false, appConstants.FAILURE);
      });
    } else {
      this.editor.saveReport().then(
        response => {
          if (response.applyAndCloseReport !== true) {
            this.editor.closeReport().catch(error => {
              log.error('close report failed : ', error);
              // Publish report close failure event
              this.publishReportingEvent(REPORTING_EVENT.CLOSE, report, false, appConstants.FAILURE);
            });
          }
        },
        error => {
          this.nprAuditService.logSaveReportFailureEvent(report, appConstants.applicationServerParticipant.NPR);
          log.debug('save failed', error);
          // Publish report close failure event
          this.publishReportingEvent(REPORTING_EVENT.CLOSE, report, false, appConstants.FAILURE);
        }
      );
    }
  }

  private showTempAccConfirmation(target) {
    this.confirmation = this.modalService.openModal(
      this.translate.instant('di.desktopIntegration.npr.temp.accession.confirmation.message'),
      {
        headerContent: this.translate.instant('di.desktopIntegration.npr.temp.accession.confirmation.title'),
        yesButtonLabel: this.translate.instant('label.continue'),
        noButtonLabel: this.translate.instant('label.cancel'),
        isDecisionModal: true,
        enableYesBtn: true
      }
    );
    const onConfirmed: Subscription = this.confirmation.afterClosed.subscribe(response => {
      onConfirmed.unsubscribe();
      if (response === 'YES') {
        target.tempAccConfirmed = true;
        this.launchDictation(target);
      } else {
        this.reportSupportService.releaseReadLock(target.id);
      }
    });
  }

  private showAddendumReportConfirmation(target) {
    this.confirmation = this.modalService.openModal(
      this.translate.instant('di.desktopIntegration.npr.addAddendum.confirmation.message'),
      {
        headerContent: this.translate.instant('di.desktopIntegration.npr.addAddendum.confirmation.title'),
        yesButtonLabel: this.translate.instant('label.addAddendum'),
        noButtonLabel: this.translate.instant('label.cancel'),
        isDecisionModal: true,
        enableYesBtn: true
      }
    );
    const onConfirmed: Subscription = this.confirmation.afterClosed.subscribe(response => {
      onConfirmed.unsubscribe();
      if (response === 'YES') {
        target.addendReportConfirmed = true;
        this.launchDictation(target);
      } else {
        this.reportSupportService.releaseReadLock(target.id);
      }
    });
  }

  private until(isValid, maxWaitTime?) {
    const promise = new Promise(resolve => {
      let elapsedTime = 0;
      const interval = 200;
      const checker = setInterval(() => {
        if (isValid()) {
          clearInterval(checker);
          resolve(true);
        } else if (maxWaitTime) {
          elapsedTime += interval;
          if (elapsedTime >= maxWaitTime) {
            clearInterval(checker);
            resolve(false);
          }
        }
      }, interval);
    });
    return promise;
  }

  private showEmergencyConfirmation(target) {
    this.confirmation = this.modalService.openModal(
      this.translate.instant('di.desktopIntegration.npr.emergency.confirmation.message'),
      {
        headerContent: this.translate.instant('di.desktopIntegration.npr.emergency.confirmation.title'),
        yesButtonLabel: this.translate.instant('label.continue'),
        noButtonLabel: this.translate.instant('label.cancel'),
        isDecisionModal: true,
        enableYesBtn: true
      }
    );
    const onConfirmed: Subscription = this.confirmation.afterClosed.subscribe(async response => {
      onConfirmed.unsubscribe();
      if (response === 'YES') {
        let associatedExams = this.tabContextService.getAssociatedExams(target.id);
        if (!associatedExams) {
          await this.until(() => {
            associatedExams = this.tabContextService.getAssociatedExams(target.id);
            return !!associatedExams;
          });
        }
        const associatedExamIds = associatedExams.map(exam => exam.examId);
        const isPendingReport = this.isPending(target.keys.reportStatus);
        this.dataService
          .discardReport({
            reportId: target.keys.reportId,
            author: this.dataLookup.getAuthorUserName(target.keys.lastAuthorId),
            accessionNumber: target.keys.procedureAccessionNo,
            examId: target.id,
            status: isPendingReport ? REPORT_STATUS.PENDING : REPORT_STATUS.ADDENDUM_PENDING,
            type: this.diConfigService.DI_CONFIG.REPORTING_INTEGRATION_TYPE
          })
          .then(
            _response => {
              this.uiEventService.broadcast('emergency.report.deleted');
              this.dataService.syncReportStatus(undefined, associatedExamIds, 0);
              if (isPendingReport) {
                target.keys.reportId = null;
                target.keys.reportStatus = 0;
                target.keys.authorId = undefined;
                target.keys.authorName = undefined;
              } else {
                target.keys.reportStatus = REPORT_STATUS.FINAL;
                target.addendReportConfirmed = true;
              }
              this.launchDictation(target);
            },
            error => log.error('Failed to discard the pending report', error)
          );
      } else {
        this.reportSupportService.releaseReadLock(target.id);
      }
    });
  }

  /**
   * Method to handle launchDictation action action.
   */
  public launchDictation(target) {
    try {
      this.healthReporter.showWaiting();
      let accession = target.keys.procedureAccessionNo;
      const reportStatus = target.keys.reportStatus ? target.keys.reportStatus : REPORT_STATUS.PENDING;
      const reportId = target.keys.reportId ? '' + target.keys.reportId : null;
      if (reportId !== null) {
        if (
          (this.isPending(reportStatus) || this.isAddendumPending(reportStatus)) &&
          target.keys.lastAuthorId &&
          target.keys.lastAuthorId !== radiologist.id
        ) {
          this.showEmergencyConfirmation(target);
          return;
        }
        //If user is trying to open a finalized report from worklist
        //they will be notified of the same with a modal dialog
        if (
          reportStatus === REPORT_STATUS.FINAL &&
          // 'undefined' indicates double click on worklist
          target.keys.source === undefined &&
          target.addendReportConfirmed !== true
        ) {
          this.showAddendumReportConfirmation(target);
          return;
        }
      } else if (target.tempAccConfirmed !== true && !accession) {
        this.showTempAccConfirmation(target);
        return;
      }
      const id = this.reportContextService.getCurrentRequestedProcId();
      const previousReport = this.workingItems.find(id);
      if (previousReport) {
        // Publish previous report close success event
        this.publishReportingEvent(REPORTING_EVENT.CLOSE, previousReport, true, appConstants.SUCCESS);
      }
      log.info('launchReport for accession =', accession, ', reportId =', reportId);
      const associationList = this.getCandidatesForAssociation(target);
      if (reportId === null) {
        log.info('launchReport - Create new report');
        this.createReport(accession, associationList, target);
      } else if (reportStatus === REPORT_STATUS.FINAL) {
        log.info('launchReport - Create addendum report');
        this.reportContextService.setCurrentContext(target.id, accession);
        this.editor
          .addendReport(reportId)
          .then((response: any) => {
            this.workingItems.add(reportId, accession, REPORT_STATUS.ADDENDUM_PENDING, target);
            this.dataService
              .saveReport(reportId, accession, target.id, REPORT_STATUS.ADDENDUM_PENDING, response.requestId)
              .then(_ => {
                const associatedExams = this.tabContextService.getAssociatedExams(target.id);
                if (associatedExams) {
                  const examIds = associatedExams.map(exam => exam.examId);
                  this.dataService.syncReportStatus(reportId, examIds, REPORT_STATUS.ADDENDUM_PENDING);
                }
              });
            this.openReportSuccessHandler(response, associationList, target);
          })
          .catch(response => {
            //working item not added, log directly
            this.auditService.saveReportAuditAction(
              this.auditService.getActionCode().Execute,
              this.auditService.getActionType().ReportSaveFailure,
              this.auditService.getEventOutcome().SeriousFailure,
              reportId,
              appConstants.applicationServerParticipant.NPR,
              reportStatus,
              undefined,
              undefined,
              target.id
            );
            log.error('Failed to create addendum report for exam', accession, ' - reason:', response);
            this.openReportErrorHandler(response, reportId, reportStatus, accession, target.id);
          });
      } else {
        log.info('launchReport - Load report contents, status =', reportStatus);
        if (!accession) {
          accession = this.translate.instant('label.temp') + target.id;
          this.reportContextService.setCurrentContext(target.id, accession);
          this.editor
            .openReport(reportId)
            .then(response => {
              this.workingItems.add(reportId, accession, reportStatus, target);
              this.openReportSuccessHandler(response, [], target);
            })
            .catch(response => this.openReportErrorHandler(response, reportId, reportStatus, accession, target));
        } else if (this.isTemporary(reportStatus)) {
          this.dataService
            .relinkReport(reportId, target.id, accession, target.keys.issuerId)
            .then(
              response => {
                log.debug('relink result =', response);
                if (response.success) {
                  this.reportContextService.setCurrentContext(target.id, accession);
                  this.editor
                    .openReport(reportId)
                    .then(repsonse => {
                      const status = reportStatus - REPORT_STATUS.TEMPORARY_SIGN;
                      this.workingItems.add(reportId, accession, status, target);
                      this.nprAuditService.logUpdateReportSuccessEvent(
                        this.workingItems.get(reportId),
                        appConstants.applicationServerParticipant.NPR
                      );
                      this.dataService.saveReport(reportId, accession, target.id, status, repsonse.requestId);
                      this.openReportSuccessHandler(response, [], target);
                    })
                    .catch(response => {
                      this.openReportErrorHandler(response, reportId, reportStatus, accession, target);
                      log.error('open report failed : ', response);
                    });
                } else {
                  throw response.error;
                }
              },
              error => {
                throw error;
              }
            )
            .catch(error => {
              log.warn('Cannot relink report', reportId, 'with new exam', accession, 'at the moment - error:', error);
              log.info('Fall back to open with current info for temporary report', reportId, 'of exam', accession);
              this.reportContextService.setCurrentContext(target.id, accession);
              this.editor.openReport(reportId).then(
                response => {
                  this.workingItems.add(reportId, accession, reportStatus, target);
                  this.nprAuditService.logUpdateReportFailureEvent(
                    this.workingItems.get(reportId),
                    appConstants.applicationServerParticipant.NPR
                  );
                  this.openReportSuccessHandler(response, [], target);
                },
                error => {
                  //working item not added, log directly
                  this.auditService.saveReportAuditAction(
                    this.auditService.getActionCode().Update,
                    this.auditService.getActionType().ReportUpdationFailure,
                    this.auditService.getEventOutcome().MajorFailure,
                    reportId,
                    appConstants.applicationServerParticipant.NPR,
                    undefined,
                    undefined,
                    undefined,
                    target.id
                  );
                  log.debug('report updation failed', error);
                  // Publish report open failure event
                  const report = { accessionNumber: accession };
                  this.publishReportingEvent(REPORTING_EVENT.OPEN, report, false, appConstants.FAILURE);
                }
              );
            });
        } else {
          const report = this.workingItems.find(target.id);
          this.reportContextService.setCurrentContext(target.id, accession);
          // If report is in TEMPLATE_SELECTING stage then only call createReport for SR otherwise request to open report
          if (report && report.status === REPORT_STATUS.TEMPLATE_SELECTING) {
            this.createReport(accession, associationList, target);
          } else {
            this.editor
              .openReport(reportId)
              .then(response => {
                this.workingItems.add(reportId, accession, reportStatus, target);
                this.openReportSuccessHandler(response, associationList, target);
              })
              .catch(response => this.openReportErrorHandler(response, reportId, reportStatus, accession, target));
          }
        }
      }
      this.reportContextService.setReportLaunchTime(target.keys.studyClickTime);
    } catch (error) {
      log.error('failed on launchDictation event with error:', error);
    }
  }

  private createReport(
    accession: any,
    associationList: { accessionNumber: any; scanModality: any; bodyParts: any; examId: any }[],
    target: any
  ) {
    const requestId = this.authenticator.createUUID();
    const examId = target.id;
    const reportText = this.reportButtonInfo.tracker.reportData;
    const approvalTime = this.reportButtonInfo.tracker.approvalTime;
    const approverName = this.reportButtonInfo.tracker.approverName;
    const isLegacy = reportText && target.keys.procedureStatusId === 90;
    let report = undefined;
    this.dataService.createReport(examId, accession, requestId, isLegacy).then(
      response => {
        const reportInfo = response.results[0];
        const reportId = '' + reportInfo.reportId;
        const accession = reportInfo.accessionNumber;
        const associatedExams = associationList.map(exam => {
          const item = Object.assign({}, exam);
          delete item.examId;
          return item;
        });
        const params: any = {
          version: this.srConfig.apiVersion,
          reportId: reportId,
          requestId: requestId,
          associatedExams: associatedExams,
          site: this.siteInfo.name,
          department: this.getDepartment(target.keys.organizationId),
          locales: [this.workingLocale]
        };
        if (reportInfo.status === REPORT_STATUS.PENDING_TEMPORARY) {
          params.associatedExams[0].accessionNumber = accession;
          params.patientInformation = this.getPatientInfo(target);
        }
        this.reportContextService.setCurrentContext(examId, accession);

        // If report_text is present and exam_stat is COMPLETED
        // Then proceed with creating a legacy report
        if (isLegacy) {
          params.legacyReportSection = {
            text: reportText,
            signature: {
              userSignature: approverName,
              timestamp: approvalTime
            }
          };
          report = this.workingItems.add(reportId, accession, REPORT_STATUS.ADDENDUM_PENDING, target);
        } else {
          report = this.workingItems.add(reportId, accession, REPORT_STATUS.TEMPLATE_SELECTING, target);
          report.isTemporary = reportInfo.status !== REPORT_STATUS.PENDING;
        }

        this.workingItems.requestParams = params;
        this.editor
          .createReport(params)
          .then(response => this.createReportSuccessHandler(response, associationList))
          .catch(response => this.createReportErrorHandler(response, associationList));
      },
      _error => {
        // Publish report open failure
        const report = { accessionNumber: accession };
        this.publishReportingEvent(REPORTING_EVENT.OPEN, report, false, appConstants.FAILURE);
        this.healthReporter.showLastFailed();
      }
    );
  }

  private cleanupAfterFailure(response, report, tab, associationList?) {
    if (report) {
      this.dataService
        .deleteReport(report.reportId, report.accessionNumber, report.examId, response.requestId)
        .then(_response => {
          this.workingItems.remove(report.reportId);
          if (associationList && associationList.length > 1) {
            this.uiEventService.broadcast('create.association.failed', {
              id: report.examId,
              associationList: associationList.map(exam => exam.accessionNumber)
            });
          }
        })
        .catch(reason => {
          log.error('got exception', reason);
        });
      // Publish report open failure event
      this.publishReportingEvent(REPORTING_EVENT.OPEN, report, false, appConstants.FAILURE);
    }
    this.reportContextService.removeContext(tab.id);
    this.reportSupportService.releaseReadLock(tab.id);
  }

  private configurationErrorHandler(response, report) {
    if (['site-does-not-exist', 'department-does-not-exist'].includes(response.errorCode)) {
      const id = response.errorCode.split('-')[0];
      const placeholder = '[' + id + ']';
      const details = id === 'site' ? this.siteInfo.name : this.siteInfo.department;
      this.setSystemError('di.desktopIntegration.npr.error.' + id + '.misconfigured', placeholder, details, true);
    } else if (response.errorCode === 'report-id-already-exists') {
      log.error('Failed to create report. Report ID already exists. Error:', response);
      const message =
        this.translate
          .instant('di.desktopIntegration.npr.warning.create.report.failed')
          .replace('[accession#]', report.accessionNumber) +
        this.translate
          .instant('di.desktopIntegration.npr.warning.reason.report.exists')
          .replace('[id#]', report.reportId) +
        ' ' +
        this.translate.instant('di.desktopIntegration.npr.warning.data.mismatch');
      this.setSystemError(message, '', '', true);
    } else if (response.errorCode === 'exam-already-associated') {
      log.error('Failed to create report.', report.accessionNumber, 'is used in another report', response);
      const message =
        this.translate
          .instant('di.desktopIntegration.npr.warning.create.report.failed')
          .replace('[accession#]', report.accessionNumber) +
        this.translate.instant('di.desktopIntegration.npr.warning.reason.exam.associated') +
        ' ' +
        this.translate.instant('di.desktopIntegration.npr.warning.data.mismatch');
      this.setSystemError(message, '', '', true);
    } else {
      log.error('Unexpected error', response);
    }
  }

  private createReportSuccessHandler(response, associationList) {
    const report = this.workingItems.get(response.reportId);
    if (report) {
      if (associationList) {
        if (associationList.length > 1) {
          this.dataService.syncAssociation(associationList, report.status);
        }
        report.associatedExamList = this.prepareAssociatedExamList(associationList);
        this.publishReportAssociation(report);
      }
      // Publish report open success event
      report.status = report.isTemporary ? REPORT_STATUS.PENDING_TEMPORARY : REPORT_STATUS.PENDING;
      this.publishReportingEvent(REPORTING_EVENT.OPEN, report, false, appConstants.SUCCESS);
    }
  }

  private createReportUnspecifiedErrorHandler(response) {
    const tab = this.utilService.getActiveTab();
    const report = this.workingItems.find(tab.id);
    if (report) {
      this.configurationErrorHandler(response, report);
      this.cleanupAfterFailure(response, report, tab);
    }
  }

  createReportErrorHandler(response, associationList) {
    const tab = this.utilService.getActiveTab();
    log.debug('createReportErrorHandler - tab =', tab);
    if (tab !== undefined && tab.workItem !== undefined) {
      const report = this.workingItems.find(tab.id);
      log.debug('createReportErrorHandler - report =', report);
      if (report !== undefined) {
        if (response.code === 404 && response.errorCode === 'exam-not-found') {
          log.error('Failed to create report - reporting system has no order info for exam', report.accessionNumber);
          const reason = this.translate.instant('di.desktopIntegration.npr.audit.order.unavailable');
          // log
          this.nprAuditService.logCreateReportFailureEvent(
            report,
            appConstants.applicationServerParticipant.ReportingSystem,
            reason
          );
          if (this.siteInfo.allowExceptionReport && associationList.length === 1) {
            const params = this.workingItems.requestParams;
            params.patientInformation = this.getPatientInfo(tab.workItem);
            log.info('Create unspecified report with params =', params);
            this.editor
              .createReport(params)
              .then(response => {
                this.dataService.saveReport(
                  report.reportId,
                  report.accessionNumber,
                  report.examId,
                  REPORT_STATUS.PENDING_TEMPORARY,
                  response.requestId
                );
                report.isTemporary = true;
                this.createReportSuccessHandler(response, associationList);
              })
              .catch(response => this.createReportUnspecifiedErrorHandler(response));
            return;
          }
          if (associationList.length > 1) {
            this.notify.warning(
              this.translate.instant('di.desktopIntegration.npr.associated.exam.order.unavailable'),
              this.translate.instant('di.desktopIntegration.title')
            );
          } else {
            this.notify.warning(reason, this.translate.instant('di.desktopIntegration.title'));
          }
        } else {
          this.configurationErrorHandler(response, report);
        }
        // Delete report for all other error codes
        this.cleanupAfterFailure(response, report, tab, associationList);
      }
    }
  }

  private openReportSuccessHandler(response, associationCandidates, target) {
    let mismatch = 0;
    const report = this.workingItems.get(response.reportId);
    if (report) {
      this.editor.getAssociatedExams(this.dataService).then(result => {
        result.exams = result.orders;
        if (result.exams && result.exams.length > 0) {
          const match = result.exams.find(exam => exam.accessionNumber === report.accessionNumber);
          if (!match) {
            mismatch |= INFO_MISMATCH.EXAM_ACCESSION;
          }
          report.associatedExamList = this.prepareAssociatedExamList(
            this.tabContextService.getAssociatedExams(report.examId)
          );
          const associationsFromSR = result.exams.map(exam => exam.accessionNumber);
          if (associationCandidates && associationCandidates.length > 1) {
            const toBeAssociatedExams = associationCandidates.filter(
              exam => !associationsFromSR.includes(exam.accessionNumber)
            );
            if (toBeAssociatedExams.length > 0) {
              this.addAssociationList(toBeAssociatedExams).then((result: any) => {
                report.associatedExamList = report.associatedExamList.concat(
                  this.prepareAssociatedExamList(result.success)
                );
                this.publishReportAssociation(report);
                // Publish report open success event
                this.publishReportingEvent(REPORTING_EVENT.OPEN, report, false, appConstants.SUCCESS);
              });
              return;
            }
          }
          this.publishReportAssociation(report);
          // Publish report open success event
          this.publishReportingEvent(REPORTING_EVENT.OPEN, report, false, appConstants.SUCCESS);
        }
        if (result.patient.patientId !== target.keys.mrn[0].value) {
          mismatch |= INFO_MISMATCH.PATIENT_MRN;
        }
        if (mismatch) {
          this.editor.showWarning(INFO_MISMATCH_WARNINGS[mismatch].replace('[id#]', report.reportId));
        }
      });
    }
  }

  private openReportErrorHandler(response, reportId, reportStatus, accession, target) {
    // Publish report open failure event
    this.publishReportingEvent(REPORTING_EVENT.OPEN, { accessionNumber: accession }, false, appConstants.FAILURE);
    if (response.errorCode === 'report-not-found') {
      if (reportStatus < REPORT_STATUS.FINAL) {
        this.setSystemError('di.desktopIntegration.npr.warning.report.not.found', '[accession#]', accession, true);
        const report = {
          reportId: reportId,
          status: reportStatus,
          auditData: this.auditService.getExamAuditInfo(target)
        };
        const reason = this.translate.instant('di.desktopIntegration.npr.audit.delete.report.not.found');
        this.dataService
          .deleteReport(reportId, accession, target.id)
          .then(_ => {
            this.nprAuditService.logDeleteReportSuccessEvent(
              report,
              appConstants.applicationServerParticipant.NPR,
              reason
            );
          })
          .catch(error => {
            log.error('Failed to clear missing report info in NPR', error);
            this.nprAuditService.logDeleteReportFailureEvent(
              report,
              appConstants.applicationServerParticipant.NPR,
              reason
            );
          });
      } else {
        this.setSystemError('di.desktopIntegration.npr.critical.data.inconsistency', '[accession#]', accession, true);
      }
    } else if (response.errorCode === 'report-locked') {
      // This case should never happen because exam lock is created first
      // Just in case if report is being opened by external tools outside WFM
      this.notify.warning(
        this.translate.instant('di.desktopIntegration.npr.warning.report.locked'),
        this.translate.instant('di.desktopIntegration.title')
      );
    }
    this.reportSupportService.releaseReadLock(target.id);
  }

  private reportButtonInfo = new (class {
    state: IReportButtonState = { isEnabled: false, label: '' };
    tracker = {
      status: undefined,
      owner: undefined,
      reportData: undefined,
      approvalTime: undefined,
      approverName: undefined,
      lastAuthorId: undefined
    };
  })();

  public getReportBtnState(reportInfo: any) {
    const isOwner = reportInfo.lastAuthorId
      ? reportInfo.lastAuthorId === radiologist.id
      : reportInfo.reportAuthor === radiologist.id;
    const author = this.dataLookup.getAuthor(reportInfo.approverId);
    const tracker = {
      status: reportInfo.reportStatus,
      owner: isOwner,
      reportData: reportInfo.reportText,
      approvalTime: reportInfo.approvalTime,
      approverName: author ? author.name : null,
      lastAuthorId: reportInfo.lastAuthorId ? reportInfo.lastAuthorId : reportInfo.reportAuthor
    };
    if (!_.isEqual(tracker, this.reportButtonInfo.tracker)) {
      this.reportButtonInfo.tracker = tracker;
      const state = { isEnabled: false, label: this.translate.instant(REPORT_BUTTON_LABELS.report) };
      const tab = this.utilService.getActiveTab();

      // For legacy report scenario report button label should be 'Add Addendum'
      if (
        reportInfo.reportText &&
        radiologist.isAllowedTo(NPR_PRIVILEGES.ADDEND) &&
        tab?.workItem?.keys &&
        tab.workItem.keys.procedureStatusId === 90 &&
        !tab.workItem.keys.reportId &&
        !tab.workItem.keys.state.isAssignedToOther
      ) {
        if (reportInfo.approvalTime && tracker.approverName) {
          state.isEnabled = true;
          state.label = this.translate.instant(REPORT_BUTTON_LABELS.addendReport);
          this.reportButtonInfo.state = state;
          return state;
        } else {
          // In case of legacy report scenario if approval_dttm or prim_approver_stid is null or invalid
          // Then report button should be disabled
          state.isEnabled = false;
          state.label = this.translate.instant(REPORT_BUTTON_LABELS.addendReport);
          this.reportButtonInfo.state = state;
          return state;
        }
      } else {
        const statusId = reportStatusIdMap[reportInfo.reportStatus];
        const permission = reportPermisionRules[statusId];
        log.debug(
          'reportStatus:',
          reportInfo.reportStatus,
          'reportAuthor:',
          reportInfo.reportAuthor,
          'current radiologist.id:',
          radiologist.id,
          'statusId:',
          statusId,
          'permission:',
          permission
        );

        if (statusId && permission) {
          if (
            (this.isPending(reportInfo.reportStatus) && radiologist.isAllowedTo(NPR_PRIVILEGES.CREATE)) ||
            (this.isAddendumPending(reportInfo.reportStatus) && radiologist.isAllowedTo(NPR_PRIVILEGES.ADDEND))
          ) {
            const tab = this.utilService.getActiveTab();
            if (tab && tab.workItem && tab.workItem.keys && !tab.workItem.keys.state.isAssignedToOther) {
              state.isEnabled = true;
              state.label = this.translate.instant(
                this.isPending(reportInfo.reportStatus)
                  ? isOwner
                    ? REPORT_BUTTON_LABELS.editReport
                    : REPORT_BUTTON_LABELS.createReport
                  : isOwner
                  ? REPORT_BUTTON_LABELS.editAddendReport
                  : REPORT_BUTTON_LABELS.addendReport
              );
              this.reportButtonInfo.state = state;
              return state;
            }
          }

          state.isEnabled = permission.isAllowed(isOwner);
          const index = state.isEnabled ? 0 : 1;
          if (permission.label && permission.label.length > index) {
            state.label = this.translate.instant(permission.label[index]);
          }
        }
      }
      this.reportButtonInfo.state = state;
    }
    return this.reportButtonInfo.state;
  }

  private tokenSync() {
    log.info('tokenSync - start');
    this.editor.syncToken();
    this.settings.syncToken();
    this.adminSettings.syncToken();
    log.info('tokenSync - stop');
  }

  public isReportPending(accNo: string): boolean {
    const report = this.workingItems.find({ accessionNumber: accNo });
    return report ? this.isPendingOrTemplateSelecting(report.status) : false;
  }

  public releaseControl(target: any): Promise<any> {
    const report = this.workingItems.find({ accessionNumber: target.accessionNo });
    if (!report) {
      return Promise.resolve(target);
    }

    return new Promise((resolve, reject) => {
      const isPendingReport = this.isPending(report.status) || this.isAddendumPending(report.status);
      if (isPendingReport) {
        log.info('releaseControl - sending request to delete report for exam', report.examId, report.accessionNumber);
        this.editor.deleteReport().then(
          () => {
            const auditData = this.auditService.getExamAuditInfo(target);
            this.auditService.saveReportAuditAction(
              this.auditService.getActionCode().Delete,
              this.auditService.getActionType().ReportDeletion,
              this.auditService.getEventOutcome().Success,
              report.reportId,
              appConstants.applicationServerParticipant.ReportingSystem,
              undefined,
              undefined,
              auditData
            );
            log.info('releaseControl - success');
            resolve(target);
          },
          error => {
            log.info('releaseControl - failed,', error);
            reject(error.description);
          }
        );
      } else {
        const releaseReport: Promise<any> =
          target.releaseOption === 'save' || (target.releaseOption === 'auto' && this.isSaveOnReleaseControl())
            ? this.editor.saveReport().catch(error => log.error('save report failed : ', error))
            : this.editor.discardReport().catch(error => log.error('discard report failed : ', error));

        releaseReport.then(
          response => {
            if (response.applyAndCloseReport !== true) {
              this.editor.closeReport().catch(error => log.error('close report failed : ', error));
            }
            resolve(target);
          },
          error => {
            log.info('releaseControl - failed,', error);
            reject(error.description);
          }
        );
      }
      this.reportContextService.removeContext(report.examId);
    });
  }

  private closeItem(report: any, requestId: string) {
    if (report) {
      log.info('Close report:', report);
      if (report.status === REPORT_STATUS.TEMPLATE_SELECTING) {
        log.info('delete pre-creating report in template selection step');
        this.dataService.deleteReport(report.reportId, report.accessionNumber, report.examId, requestId).then(
          _ => {
            this.nprAuditService.logDeleteReportSuccessEvent(report, appConstants.applicationServerParticipant.NPR);
          },
          error => {
            this.nprAuditService.logDeleteReportFailureEvent(report, appConstants.applicationServerParticipant.NPR);
            log.error('Error while deleting report', error);
          }
        );
      } else if (this.isPending(report.status) || this.isAddendumPending(report.status)) {
        if (!this.isSaveOnTabClosure()) {
          log.info('Discard & delete pending report');
          const isAddendumPending = this.isAddendumPending(report.status);
          this.dataService.discardReports([report], requestId).then(
            _ => {
              const examIds = this.getReportExamIds(report);
              if (isAddendumPending) {
                this.dataService.syncReportStatus(report.reportId, examIds, REPORT_STATUS.FINAL);
              } else {
                this.dataService.syncReportStatus(undefined, examIds, 0);
              }
              this.nprAuditService.logDeleteReportSuccessEvent(report, appConstants.applicationServerParticipant.NPR);
            },
            error => {
              this.nprAuditService.logDeleteReportFailureEvent(report, appConstants.applicationServerParticipant.NPR);
              log.error('Error while deleting report', error);
            }
          );
          report.status = isAddendumPending ? REPORT_STATUS.FINAL : REPORT_STATUS.UNKNOWN;
        }
      } else if (this.isNotFinal(report.status)) {
        if (!this.isSaveOnTabClosure()) {
          log.info('Discard report per user preferences');
          this.dataService.discardReports([report], requestId);
        } else {
          log.info('Save report');
          this.dataService.saveReports([report], requestId).then(
            _ => {
              this.nprAuditService.logSaveReportSuccessEvent(report, appConstants.applicationServerParticipant.NPR);
            },
            error => {
              this.nprAuditService.logSaveReportFailureEvent(report, appConstants.applicationServerParticipant.NPR);
              log.debug('Failed to save reports', error);
            }
          );
        }
      }
      this.workingItems.remove(report.reportId);
    }
  }

  private workingItems = {
    data: {},
    add: (reportId: string, accession: string, status: number, target: any) => {
      if (!(accession in this.workingItems.data)) {
        const report: any = {
          reportId: reportId,
          author: radiologist.userName,
          accessionNumber: accession,
          organization: radiologist.domain,
          examId: this.reportContextService.getCurrentRequestedProcId(),
          status: status,
          type: this.diConfigService.DI_CONFIG.REPORTING_INTEGRATION_TYPE,
          auditData: this.auditService.getExamAuditInfo(target)
        };
        report.isTemporary = this.isTemporary(status);
        this.workingItems.data[reportId] = report;
        log.debug('Add', accession, 'to tracking list:', report);
        return report;
      }
    },
    get: (reportId: string) => {
      return this.workingItems.data[reportId];
    },
    find: (id: any) => {
      if (typeof id === 'object') {
        return _.findWhere(this.workingItems.data, id);
      }
      return _.findWhere(this.workingItems.data, { examId: id });
    },
    remove: (reportId: string) => {
      const v = this.workingItems.data[reportId];
      if (v) {
        log.debug('Remove', v.accessionNumber, 'from tracking list:', v);
        delete this.workingItems.data[reportId];
      }
    },
    clear: () => {
      this.workingItems.data = {};
    },
    values: () => {
      return _.values(this.workingItems.data);
    },
    isEmpty: () => {
      return _.isEmpty(this.workingItems.data);
    },
    requestParams: undefined
  };

  private healthStatus = this.statusService.healthStatusEnum;
  private healthReporter = {
    showReady: () => {
      if (this.currentStatus === WORKING_STATUS.OK) {
        if (this.defaultLocaleSupported) {
          this.healthReporter.set(this.healthStatus.ready);
          this.serviceStatus = SERVICE_STATUS.Enabled;
        } else {
          this.healthReporter.showUnsupportedLanguage();
        }
      }
    },
    showWaiting: (action?: string) => {
      if (this.currentStatus === WORKING_STATUS.OK) {
        this.healthReporter.set(this.healthStatus.pendingResponse, action);
        this.serviceStatus = SERVICE_STATUS.LaunchApplicationInitiated;
      }
    },
    showLastFailed: () => {
      if (this.currentStatus === WORKING_STATUS.OK) {
        this.healthReporter.set(this.healthStatus.lastActionFailed);
        this.serviceStatus = SERVICE_STATUS.None;
        // Release read lock for the active exam
        this.reportSupportService.releaseReadLock();
      }
    },
    showFailed: () => {
      this.healthReporter.set(this.healthStatus.failed);
      this.serviceStatus = SERVICE_STATUS.Disabled;
      // Release read lock for the active exam
      this.reportSupportService.releaseReadLock();
    },
    showUnsupportedLanguage: () => {
      if (this.currentStatus === WORKING_STATUS.OK) {
        this.healthReporter.set(this.healthStatus.unsupportedLanguage);
        this.serviceStatus = SERVICE_STATUS.None;
      }
    },
    set: (value: string, action?: string) => {
      this.statusService.setReportingHealthStatus(value, action);
    }
  };

  // #region Association handling methods
  private getCandidatesForAssociation(context) {
    const primaryExam = {
      accessionNumber: context.keys.procedureAccessionNo,
      scanModality: context.keys.modality,
      bodyParts: context.keys.bodyRegion ? context.keys.bodyRegion.split(',') : [],
      examId: context.id,
      procedure: context.keys.procedureCode
    };
    const associatedExamList = [primaryExam];
    if (primaryExam.accessionNumber) {
      const associableExams = this.tabContextService.getAssociableExamsState(context.id);
      if (associableExams) {
        _.each(_.keys(associableExams), accession => {
          const exam = associableExams[accession];
          if (exam.isAssociated === 'Y') {
            associatedExamList.push({
              accessionNumber: accession,
              examId: exam.requestedProcedureId,
              scanModality: exam.modality,
              bodyParts: exam.bodyParts ? exam.bodyParts.split(',') : [],
              procedure: exam.procedureCode
            });
          }
        });
      }
    }
    // This ensures that the `issuerId` is included in the associated exams when issueridRequired is set to true
    if (this.srConfig.issueridRequired) {
      const issuerId = this.getInstitute(context.keys.instituteId);
      if (!issuerId) {
        log.debug('Issuer information is not present for context', context.id);
      } else {
        _.each(associatedExamList, exam => {
          exam.issuerId = issuerId;
        });
      }
    }
    return associatedExamList;
  }

  private publishReportAssociation(report) {
    let accessionList = [report.accessionNumber];
    if (report.associatedExamList && report.associatedExamList.length > 0) {
      accessionList = report.associatedExamList.map(exam => exam.accessionNumber);
      const associatedExams = [];
      _.each(report.associatedExamList, exam => {
        associatedExams.push({
          reportId: report.reportId,
          accessionNumber: exam.accessionNumber,
          examId: exam.examId
        });
      });
      this.tabContextService.setAssociatedExams(report.examId, associatedExams);
    }
    this.uiEventService.broadcast('associationFromReportingApp', {
      id: report.examId,
      accession: accessionList.join()
    });
  }

  public isReadyForAssociation() {
    const reportId = this.editor.getReportId();
    const report = this.workingItems.get(reportId);
    return report && report.status !== REPORT_STATUS.TEMPLATE_SELECTING;
  }

  private addAssociationList(exams) {
    return new Promise(resolve => {
      const result = { success: [] };
      const adder = async idx => {
        try {
          const exam = await this.associateExam(exams[idx]);
          result.success.push(exam);
        } catch (error) {
          //
        }
        if (idx < exams.length - 1) {
          adder(idx + 1);
        } else {
          resolve(result);
        }
      };
      adder(0);
    });
  }

  public examAssociation(exam) {
    if (!this.srConfig.issueridRequired) {
      delete exam.issuerId; // Remove issuerId if issuerIdRequired flag is set to false
    }
    if (exam.isAssociated === 'Y') {
      this.associateExam(exam)
        .then(response => {
          const report = this.workingItems.get(response.reportId);
          if (report && report.associatedExamList) {
            if (!_.findWhere(report.associatedExamList, { examId: exam.examId })) {
              report.associatedExamList.push({ examId: exam.examId, accessionNumber: exam.accessionNumber });
            }
            this.publishReportAssociation(report);
          }
        })
        .catch(error => log.error('associate exam failed : ', error));
    } else if (exam.isAssociated === 'N') {
      this.disassociateExam(exam).then(response => {
        if (response?.reportId) {
          const report = this.workingItems.get(response.reportId);
          if (report && report.associatedExamList) {
            report.associatedExamList = report.associatedExamList.filter(item => item.examId !== exam.examId);
            this.publishReportAssociation(report);
          }
        }
      });
    }
  }

  private associateExam(exam) {
    return this.editor
      .addToAssociation(exam.accessionNumber, this.getInstitute(exam.issuerId), this.srConfig.issueridRequired)
      .then(
        response => {
          const report = this.workingItems.get(response.reportId);
          const reportStatus = report ? report.status : undefined;
          this.dataService.addToAssociation(response.reportId, exam.accessionNumber, exam.examId, reportStatus);
          exam.reportId = response.reportId;
          return exam;
        },
        error => {
          log.error('associateExam - addToAssociation failed : ', error);
          this.uiEventService.broadcast('add.association.failed', {
            id: exam.examId,
            accession: exam.accessionNumber
          });
          throw error;
        }
      );
  }

  private disassociateExam(exam) {
    return this.editor
      .removeFromAssociation(exam.accessionNumber, this.getInstitute(exam.issuerId), this.srConfig.issueridRequired)
      .then(
        response => {
          this.dataService.removeFromAssociation(exam.examId);
          exam.reportId = response.reportId;
          return exam;
        },
        error => {
          log.error('disassociateExam - removeFromAssociation failed : ', error);
          this.uiEventService.broadcast('remove.association.failed', {
            id: exam.examId,
            accession: exam.accessionNumber
          });
        }
      );
  }

  private prepareAssociatedExamList(associationList) {
    const associatedExamList = [];
    _.each(associationList, exam => {
      associatedExamList.push({
        examId: exam.examId,
        accessionNumber: exam.accessionNumber
      });
    });
    return associatedExamList;
  }
  // #endregion

  private publishReportingEvent(eventName, report, tabInterrupted, result) {
    if (report) {
      const accession =
        report.associatedExamList && report.associatedExamList.length > 0
          ? report.associatedExamList.map(exam => exam.accessionNumber).join()
          : report.accessionNumber;
      const reportStatus = result === appConstants.FAILURE ? REPORT_STATUS.UNKNOWN : report.status;
      this.reportSupportService.publishReportEvents(eventName, accession, reportStatus, tabInterrupted, result);
    }
  }

  private checkIfDefaultDeparmentExist() {
    this.dataLookup.findByvalue('departments', 'code', this.siteInfo.department).then(item => {
      if (item === undefined) {
        this.healthReporter.showFailed();
        this.setSystemError(
          'default.department.missing',
          '{{DEPARTMENT}}',
          this.siteInfo.department,
          true,
          'sr.service'
        );
        this.setCurrentStatus(WORKING_STATUS.NO_SR_SERVICE);
      } else {
        this.preferences.setSRDefaultDepartment(this.siteInfo.department);
      }
    });
  }

  private getDepartment(departmentId) {
    log.debug('Department id', departmentId);
    let department = '';
    if (departmentId != undefined && departmentId > 0) {
      department = this.dataLookup.getDepartments().find(item => {
        return item.id === departmentId;
      }).code;
    } else {
      department = this.siteInfo.department;
    }
    return department;
  }

  private getInstitute(instituteId) {
    log.debug('Institute id', instituteId);
    if (instituteId != undefined) {
      const institute = this.dataLookup.getInstitutes().find(item => {
        return item.id === instituteId;
      })?.code;
      return institute;
    } else {
      return instituteId;
    }
  }
}
